import React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import {
  LocationPlaces,
  DeliveryType,
  Countries,
  FTL_VOLUME,
  FTL_WEIGHT,
} from '../shared';
import { ReactComponent as SpaceIcon } from 'assets/icons/space.svg';
import { ReactComponent as PickupIcon } from 'assets/icons/pickup.svg';
import { ReactComponent as DeliveryIcon } from 'assets/icons/delivery.svg';
import { futureDate } from 'utils/date';
import { Main } from 'components/Main';
import { Box } from 'components/Box';
import { Form } from 'components/Form';
import { Heading } from 'components/Heading';
import { Footer } from 'components/Footer';
import { Button } from 'components/Button';
import { SubHeading } from './components/SubHeading';
import { ErrorProvider, Error } from 'components/Error';
import { LDM_WEIGHT } from '../shared';
import { Location } from './Location';
import { Space, SpaceType } from './Space';
import { Volume, PalletValues } from './Volume';
import { Pickup } from './Pickup';
import { Delivery } from './Delivery';

export type QuoteState = {
  space: SpaceType;
  location: LocationPlaces;
  volume: number;
  weight: number;
  pallets?: PalletValues;
  deliveryType: DeliveryType;
  pickupDate: Date;
};

const Quote = () => {
  const history = useHistory();
  const { state } = useLocation<{ quote: QuoteState }>();
  const { error: countriesError } = useSWR<Countries>('/api/country-data');

  const {
    location: defaultLocation,
    volume: defaultVolume,
    weight: defaultWeight,
    deliveryType: defaultDeliveryType = 'standard',
    pickupDate: defaultPickupDate = futureDate(3),
  } = state?.quote || {};

  const [location, setLocation] = React.useState<LocationPlaces | undefined>(
    defaultLocation
  );

  const [space, setSpace] = React.useState<SpaceType>();
  const [volume, setVolume] = React.useState<number | undefined>(defaultVolume);
  const [weight, setWeight] = React.useState<number | undefined>(defaultWeight);
  const [pallets, setPallets] = React.useState<PalletValues>();

  const deliveryVolume = React.useMemo(() => {
    const weightLdm = (weight || 0) / LDM_WEIGHT;
    const volumeLdm = volume || 0;

    return Math.max(weightLdm, volumeLdm);
  }, [volume, weight]);

  const [deliveryType, setDeliveryType] = React.useState<DeliveryType>(
    defaultDeliveryType
  );

  const [pickupDate, setPickupDate] = React.useState<Date | undefined>(
    defaultPickupDate
  );

  let form: Partial<QuoteState> = {
    space,
    location,
    volume,
    weight: space === 'FTL' ? FTL_WEIGHT : weight,
    deliveryType,
    pickupDate,
  };

  if (space === 'LTL') {
    form = { ...form, pallets };
  }

  const isValid = Object.keys(form).every(formKey => {
    const key = formKey as keyof typeof form;
    return form[key] !== undefined;
  });

  function handleSpaceTypeChange(space: SpaceType) {
    setSpace(space);

    if (space === 'FTL') {
      setVolume(FTL_VOLUME);
    } else {
      setVolume(undefined);
    }
  }

  function handleSubmit() {
    if (isValid) {
      history.push('/delivery', { quote: form as QuoteState });
    }
  }

  return (
    <StyledForm onSubmit={handleSubmit}>
      <ErrorProvider>
        <Main>
          {countriesError ? (
            <Error>
              We're currently down for maintenance. Please try again later.
            </Error>
          ) : (
            <Box margin={[0, 'auto']} maxWidth={900}>
              <Heading size="xlarge">
                Quote your delivery cost and timeframe
              </Heading>
              <Location location={location} onLocationChange={setLocation} />

              <SubHeading icon={SpaceIcon}>
                How much space do you need?
              </SubHeading>
              <Space value={space} onChange={handleSpaceTypeChange} />

              {space === 'LTL' && (
                <Volume
                  value={volume}
                  onChange={setVolume}
                  weight={weight}
                  onWeightChange={setWeight}
                  onPalletsChange={setPallets}
                />
              )}

              {Boolean(deliveryVolume) && location?.from && location?.to && (
                <>
                  <SubHeading icon={PickupIcon}>
                    Choose your pickup date
                  </SubHeading>
                  <Pickup value={pickupDate} onChange={setPickupDate} />

                  {pickupDate && (
                    <>
                      <SubHeading icon={DeliveryIcon}>
                        Choose your delivery date
                      </SubHeading>
                      <Delivery
                        volume={deliveryVolume}
                        location={location as Required<LocationPlaces>}
                        pickupDate={pickupDate}
                        delivery={deliveryType}
                        onDeliveryChange={setDeliveryType}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Main>
      </ErrorProvider>

      {Boolean(deliveryVolume) && location?.from && location?.to && (
        <Footer>
          <Button type="submit" disabled={!isValid}>
            Request delivery
          </Button>
        </Footer>
      )}
    </StyledForm>
  );
};

const StyledForm = styled(Form)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

export { Quote };
